import { breachedPasswordMessage, checkPasswordBreached } from '@auctisync/shared';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@auctisync/ui';
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../../hooks/useSession';
import { invokeEF } from '../../lib/edgeFunctions';
import { sha1Hex } from '../../lib/sha1';
import { supabase } from '../../lib/supabase';

// 'checking' is the breach lookup, which happens BEFORE anything is saved. Its
// own label so a slow HaveIBeenPwned response can't leave someone reading
// "Saving…" and believing their password was already set.
type Mode = 'idle' | 'checking' | 'submitting' | 'done';

/**
 * Where an invited auction-house owner chooses their password.
 *
 * 🔴 WHY THIS STEP CANNOT BE SKIPPED. Supabase's invite creates the account
 * WITHOUT a password. The invite link signs them in, so sending them straight to
 * the setup checklist looks like it worked — right up to the moment they close
 * the tab. There is then no password to sign back in with, and the only route
 * back is another invite. This page is what turns a one-time link into an
 * account.
 *
 * It is the exact parallel of password recovery, and is enforced the same way:
 * `AuthConfirm` sends `type=invite` here regardless of the link's `redirect_to`,
 * so an invite cannot be aimed past it.
 *
 * Recording the acceptance is a side errand, not a gate. `tenant_invites` is
 * super-admin-only by RLS (it holds the abuse trail), so an Edge Function does
 * it with the service role — and if that fails, the user still continues. Their
 * account and their ownership already exist; refusing to let them in over an
 * audit row would be the wrong trade.
 */
export function AcceptInvite() {
  const navigate = useNavigate();
  const { loading, userId } = useSession();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [mode, setMode] = useState<Mode>('idle');
  const [error, setError] = useState<string | null>(null);

  // Same race as ResetPassword: arriving straight from an email link, the
  // session is not there on the first read — the client redeems the token a beat
  // later. Bouncing immediately would kick a VALID invite to the sign-in page.
  useEffect(() => {
    if (loading || userId !== null) return;
    const bounce = setTimeout(() => {
      void navigate('/admin', { replace: true });
    }, 4000);
    return () => clearTimeout(bounce);
  }, [loading, userId, navigate]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Those two passwords don’t match.');
      return;
    }

    // Same gate as every other password path in the product: nobody may set a
    // known-breached password, and an auction-house OWNER is the account with
    // the most to lose.
    setMode('checking');
    const breach = await checkPasswordBreached(password, sha1Hex);
    if (breach.breached) {
      setMode('idle');
      setError(breachedPasswordMessage(breach.count));
      return;
    }

    setMode('submitting');
    const { error: updateError } = await supabase.auth.updateUser({ password });
    if (updateError) {
      setMode('idle');
      setError(updateError.message);
      return;
    }

    // Bookkeeping, deliberately after the password is safely set and
    // deliberately not fatal — see the note at the top.
    await invokeEF('accept-invite', {});

    setMode('done');
    await navigate('/admin/onboarding', { replace: true });
  }

  const busy = mode === 'checking' || mode === 'submitting';

  return (
    <main
      style={{
        minHeight: '70vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
      }}
    >
      <Card style={{ width: '100%', maxWidth: '26rem' }}>
        <CardHeader>
          <CardTitle>Welcome to AuctiSync</CardTitle>
          <CardDescription>
            Choose a password and we&apos;ll take you straight to setting up your auction house.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={(e) => void handleSubmit(e)}>
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="invite-password" style={labelStyle}>
                Password
              </label>
              <input
                id="invite-password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={inputStyle}
              />
              <p style={hintStyle}>At least 8 characters.</p>
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <label htmlFor="invite-confirm" style={labelStyle}>
                Confirm password
              </label>
              <input
                id="invite-confirm"
                name="confirm"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                style={inputStyle}
              />
            </div>

            {error !== null && (
              <p role="alert" style={{ color: 'var(--semantic-danger)', marginBottom: '1rem' }}>
                {error}
              </p>
            )}

            <Button type="submit" disabled={busy} style={{ width: '100%' }}>
              {mode === 'checking'
                ? 'Checking password…'
                : mode === 'submitting'
                  ? 'Saving…'
                  : 'Continue'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </main>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.85rem',
  fontWeight: 600,
  color: 'var(--text)',
  marginBottom: '0.35rem',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.55rem 0.7rem',
  borderRadius: '0.45rem',
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '0.95rem',
};

const hintStyle: React.CSSProperties = {
  fontSize: '0.78rem',
  color: 'var(--text-muted)',
  margin: '0.3rem 0 0',
};
